import React, { useState, forwardRef, useEffect, useImperativeHandle, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import styled from 'styled-components'
import { Modal, Input, Button, notification } from 'antd';
import { Icon, ThemeProvider } from 'dlweb-components';
import { showModal } from '../../../store/actions';
import _, { set } from 'lodash';
import { useTableSelectionState }  from '../../../hooks'
import GroupTable from './GroupTable';
import GroupDetails from './GroupDetails';
import NewGroupDialog from './NewGroupDialog';
import WorkflowReplacementPicker from '../../modals/WorkflowReplacementPicker';
import ActiveDirectoryGroupModal from './ActiveDirectoryGroupModal';

const GroupManager = forwardRef(({setMenuItems, isAdmin}, ref) => {

  let searchInput = null;

  const keys = useMemo(() => ['id', 'inWorkflow', 'index'], []);

  const [open, setOpen] = useState(false);
  const [newADGroupOpen, setNewADGroupOpen] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [groups, setGroups] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [importUsers, setImportUsers] = useState(true);
  const [adminSettings, setAdminSettings] = useState({});
  const [pagination, setPagination] = useState({total: 0, defaultPageSize: 500});
  const [sorterInfo, setSorterInfo] = useState({columnKey: 'ID', order: 'ascend'});
  const [replacementId, setReplacementId] = useState(null);
  const [groupsInWorkflow, setGroupsInWorkflow] = useState([]);
  const [defaultGroups] = useState(['Admins', 'Managers', 'Users']);
  const [showReplacementDialog, setShowReplacementDialog] = useState(false);
  const [selectedGroups, setSelectedGroups] = useTableSelectionState(keys, []);

  const addButton = useMemo(() => ({
    key: 'new-group',
    action: 'new-group',
    icon: <Icon type='users-medical' fixedWidth="25px" />,
    label: 'New Group'
  }), [])

  const copyButton = useMemo(() => ({
    key: 'copy-group',
    action: 'copy-group',
    icon: <Icon type='users-class' fixedWidth="25px" />,
    label: 'Copy Group'
  }), [])

  const copyADButton = useMemo(() => ({
    key: 'copy-group-ad',
    action: 'copy-group-ad',
    icon: <Icon type='users-class' fixedWidth="25px" />,
    label: 'Copy Group From AD'
  }), [])

  const getDeleteButton = useCallback(() => {
    return {
      key: 'delete-group',
      action: 'delete-group',
      icon: <Icon type='users-slash' fixedWidth="25px" />,
      label: `Delete Group${selectedGroups.length > 1 ? 's' : ''}`
    };
  }, [selectedGroups.length]);

  const deleteButton = useMemo(() => getDeleteButton(), [getDeleteButton]);

  useEffect(() => {
    axios.post('/api/settings/read/dto/wt').then((res) => {
      setAdminSettings(res.data.data)
    })
  }, [setAdminSettings])

  useEffect(() => {

    const defaultButtons = [addButton];

    if(_.map(selectedGroups, 'id').some(i => defaultGroups.includes(i))) {
      if(selectedGroups.length === 1) defaultButtons.push(copyButton);
    }
    else {
      defaultButtons.push(deleteButton);
      selectedGroups.length === 1 && defaultButtons.splice(1, 0, copyButton);
    }

    if(adminSettings.activeDirectory?.enabled) {
      selectedGroups.length === 1 && defaultButtons.splice(2, 0, copyADButton);
    }

    setMenuItems(defaultButtons);

  }, [selectedGroups, setMenuItems, addButton, adminSettings.activeDirectory?.enabled, copyButton, deleteButton, copyADButton, defaultGroups]);

  useImperativeHandle(ref, () => ({
    onButtonClick(action) {
      switch (action) {
        case 'new-group':
          setOpen(true);
          setIsCopy(false);
          break;
        default:
          handleContextClick(action);
          break;
      }
    },
  }))

  const loadGroups = useCallback((groupName, filters) => {
    setLoading(true);
    setSelectedGroups([]);

    const _filters = { ...filters };

    if(!_filters?.skip) {
      _filters.limit = 500;
      _filters.skip = 0;
    }

    if(!_filters?.order) {
      const sort = { columnKey: 'ID', order: 'ascend' };
      _filters.order = sort;
      setSorterInfo(sort);
    }

    axios.post('/api/wt/groupman/dto/group/list/read', {..._filters}).then(
      res => {

        const groupList = res.data.data.map((o, i) => ({...o, index: i}));

        const defaultSelection = groupName ? _.find(groupList, { id: groupName }) : groupList[0];

        setGroups(groupList);
        setSelectedGroups(true, true, [defaultSelection]);
        setPagination(p => ({ ...p, total: res.data._meta.totalRecords }));

        //scroll to new group
        document.querySelector('div.ant-table-body').scrollTop = _.findIndex(res.data.data, { id: groupName }) * 45;
        setLoading(false)
      }
    )
  }, [setSelectedGroups]);

  const postDelete = useCallback(() => {
    axios.post('/api/wt/groupman/dto/group/delete', { groupIds: _.map(selectedGroups, 'id'), replacementId: replacementId }).then(
      (res) => { 
        loadGroups();
        setPagination(p => ({ ...p, current: 1 }));
        setSorterInfo({columnKey: 'ID', order: 'ascend'});
        setFilteredInfo({columnKey: null, order: null});
        showReplacementDialog && closeReplacementDialog();

        if(res.data.errors.length > 0) {
          notification.error({ message: res.data.errors[0] });
          res.data.errors[1] && console.error(res.data.errors[1]);
        }
        else {
          const msg = selectedGroups.length > 1 ? `${selectedGroups.length} groups deleted.` : <span>Group <strong>{selectedGroups[0].id}</strong> deleted.</span>;

          notification.success({ message: msg });
        }
      }
    )
  }, [selectedGroups, replacementId, loadGroups, showReplacementDialog])

  const deleteSelectedGroups = useCallback(() => {
       
    const badSelection = _.find(selectedGroups, o => defaultGroups.includes(o.id));

    if(badSelection) {
      notification.error(
        {
          message:  <div>
                      Group <strong>{badSelection.id}</strong> cannot be deleted
                    </div> 
        }
      );
      return;
    }

    const giw = _.filter(selectedGroups, o => o.inWorkflow);

    if(giw.length > 0) {
      setGroupsInWorkflow(giw.map(o => o.id));
      setShowReplacementDialog(true);
      return;
    }
    else {
      postDelete();
    }

  }, [postDelete, selectedGroups, defaultGroups])

  const handleContextClick = useCallback((action) => {

    switch (action) {
      case 'copy-group':
        setIsCopy(true);
        setOpen(true);
        break;
      case 'copy-group-ad':
        setNewADGroupOpen(true);
        break;
      case 'delete-group':
        Modal.confirm({
          icon: null,
          getContainer: '#groupContainer',
          title: <>
                    <Icon type='exclamation-circle' size="lg" style={{color: 'var(--warning-color)', marginRight: '10px'}}/>
                    Delete the following group{selectedGroups.length > 1 ? 's' : ''}?
                </>,
          content: <DeleteList>
                      <ul style={{listStyle: 'none', margin: '20px 0', padding: 0, maxHeight: '200px', overflowY: 'auto'}}>
                        {selectedGroups.map((item) => (
                          <li key={item.id}>
                            <DeleteListItem>{item.id}</DeleteListItem>
                          </li>
                        ))}
                      </ul>
                    </DeleteList>,
          onOk: deleteSelectedGroups
        });
        break;
      default:
        break;
    }

  }, [selectedGroups, deleteSelectedGroups])

  const closeDialog = () => {
    setOpen(false);
  }

  const onDialogOk = (groupName) => {

    if(isCopy) {
      axios.post('/api/wt/groupman/dto/group/copy', { groupId: selectedGroups[0].id, newGroupId: groupName }).then(
        (res) => {
          const errors = res.data.errors;
  
          if(errors.length > 0) {
            notification.error({ message: errors[1] || errors[0] });
          }
          else {
            loadGroups(groupName);
            setPagination(p => ({ ...p, current: 1 }));
            setSorterInfo({columnKey: 'ID', order: 'ascend'});
            notification.success({ message: <span>Group <strong>{groupName}</strong> copied from <strong>{selectedGroups[0].id}</strong>.</span> });
          }
  
          closeDialog();
        }
      )
    }
    else {
      axios.post('/api/wt/groupman/dto/group/create', { id: groupName }).then(
        (res) => {
          const errors = res.data.errors;
  
          if(errors.length > 0) {
            notification.error({ message: errors[1] || errors[0] });
          }
          else {
            loadGroups(groupName);
            setPagination(p => ({ ...p, current: 1 }));
            setSorterInfo({columnKey: 'ID', order: 'ascend'});
            notification.success({ message: <span>Group <strong>{groupName}</strong> created.</span> });
          }
  
          closeDialog();
        }
      )
    }

    setFilteredInfo({columnKey: null, order: null});
  }

  const onDialogCancel = () => {
    closeDialog();
    setIsCopy(false);
  }

  useEffect(() => {
    loadGroups();
  }, [loadGroups])
  
  const onReplacementOk = () => {
    postDelete();
  }

  const closeReplacementDialog = () => {
    setShowReplacementDialog(false);
    setReplacementId('');
  }

  const handleGroupTableChange = (_pagination, filters, sorter) => {

    const pager = { ..._pagination };
    pager.current = _pagination.current;
    setPagination(pager);
    setSorterInfo(sorter);
    setFilteredInfo(filters);

    const filterObject = {};

    _.forEach(filters, (value, field) => {
      if(value) filterObject[field] = value[0]
    })

    const cf = {
      limit: _pagination.pageSize,
      skip: (_pagination.current - 1) * _pagination.pageSize,
      order: [{
        column: sorter.columnKey,
        direction: sorter.order === 'ascend' ? 0 : 1
      }],
      filter: filterObject
    }

    loadGroups(null, cf);
  };

  const onDetailsSave = (groupName) => {
    loadGroups(groupName);
  }

  const onAddActiveDirectoryGroup = (selectedGroups) => {

    const addADGroups = async () => {

      setLoading(true);

      const res = await axios.post('/api/wt/groupman/ad/add', { assignUsers: importUsers, adGroupIds: selectedGroups.map(o => o.samAccountName) });

      if(res.data.errors.length > 0) {
        console.error(res.data.errors[0]);
        notification.error({message: 'Could not copy group from AD.'});
      }
      else {
        const msg = selectedGroups.length > 1 ? `${selectedGroups.length} groups copied from Active Directory.` : <span>Group <strong>{selectedGroups[0].name}</strong> copied from Active Directory.</span>;

        notification.success({ message: msg });
      }

      loadGroups();
      setPagination(p => ({ ...p, current: 1 }));
      setSorterInfo({columnKey: 'ID', order: 'ascend'});
      setNewADGroupOpen(false);
    }

    addADGroups();
  }

  const getColumnSearchProps = (columnTitle, dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          placeholder={`Filter ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 90, marginRight: 8 }}
        >
          Apply
        </Button>
        <Button onClick={() => handleReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    onFilterDropdownOpenChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },
  });
  
  const handleSearch = (dataIndex, selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };

  return(
    <ThemeProvider>
      <MainContainer id="groupContainer">
        <WorkflowReplacementPicker
          onSelect={setReplacementId}
          onCancel={closeReplacementDialog}
          visible={showReplacementDialog}
          membersInWorkflow={groupsInWorkflow}
          userIds={selectedGroups}
          onOk={onReplacementOk}
          includeGroups={true}
        />
        <GroupContainer>
          <GroupTable
            loading={loading}
            groups={groups}
            pagination={pagination}
            handleTableChange={handleGroupTableChange}
            selectedGroups={selectedGroups}
            setSelectedGroups={setSelectedGroups}
            onContextClick={handleContextClick}
            contextMenuItems={[copyButton, deleteButton]}
            defaultGroups={defaultGroups}
            sorter={sorterInfo}
            filter={filteredInfo}
            getColumnSearchProps={getColumnSearchProps}
          />
        </GroupContainer>
        <GroupDetailsContainer>
          {selectedGroups.length !== 1 && <GroupDetailsOverlay />}
          <GroupDetails
            onSave={onDetailsSave}
            selectedGroups={selectedGroups}
          />
        </GroupDetailsContainer>
        <NewGroupDialog
          open={open}
          groups={groups}
          isCopy={isCopy}
          selectedGroups={selectedGroups}
          onOk={onDialogOk}
          onCancel={onDialogCancel}
        />
        <ActiveDirectoryGroupModal
          open={newADGroupOpen}
          importUsers={importUsers}
          setImportUsers={setImportUsers}
          onOk={onAddActiveDirectoryGroup}
          closeDialog={() => setNewADGroupOpen(false)}
          onCancel={() => setNewADGroupOpen(false)}
        />
      </MainContainer>
    </ThemeProvider>
  )
})

const MainContainer = styled.div`
  height: 100%;
  display: flex;
  .ant-modal-confirm-body {
    display: block;
  }
`

const GroupContainer = styled.div`
  height: 100%;
  width: 350px;
  flex: 0 0 350px;
  overflow-x: hidden;
  border-right: solid 1px #ccc;
`
const GroupDetailsContainer = styled.div`
  flex: 1 1;
  overflow: hidden;
  padding: 10px 20px;
  position: relative;
`

const GroupDetailsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.6);
  z-index: 100;
`

const DeleteList = styled.div`
  overflow: hidden;
`

const DeleteListItem = styled.div`
  overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const mapStateToProps = state => {
  const { isAdmin, user } = state.user;
  return { isAdmin, user };
};

const mapDispatchToProps = { showModal };

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(GroupManager);